import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, KeyRound, Check, Shield, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = 'https://admin.afriperx.online/api';
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [fallbackCode, setFallbackCode] = useState('');
  const [emailSent, setEmailSent] = useState(true);
  const [resetToken, setResetToken] = useState('');
  const [suggestedPasscode, setSuggestedPasscode] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');

  const handleSendOtp = async () => {
    if (!mobile.trim() || mobile.length < 7) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password/send-otp`, { mobile });
      setMaskedEmail(res.data.email_masked || '');
      setEmailSent(res.data.email_sent);
      if (!res.data.email_sent && res.data.otp_fallback) {
        setFallbackCode(res.data.otp_fallback);
      }
      setStep(2);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim() || otp.length < 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password/verify-otp`, { mobile, otp });
      setResetToken(res.data.reset_token);
      setSuggestedPasscode(res.data.suggested_passcode || '');
      setNewPasscode(res.data.suggested_passcode || '');
      setConfirmPasscode(res.data.suggested_passcode || '');
      setStep(3);
      toast.success('Code verified! Set your new passcode.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasscode = async () => {
    if (newPasscode.length !== 4 || !/^\d{4}$/.test(newPasscode)) {
      toast.error('Passcode must be exactly 4 digits');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      toast.error('Passcodes do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/reset`, {
        reset_token: resetToken,
        new_passcode: newPasscode,
      });
      setStep(4);
      toast.success('Passcode reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset passcode');
    } finally {
      setLoading(false);
    }
  };

  const useSuggested = () => {
    setNewPasscode(suggestedPasscode);
    setConfirmPasscode(suggestedPasscode);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-amber-50/30" data-testid="forgot-password-page">
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="AfriPerx" className="h-10 w-auto" />
          </Link>
          <button onClick={() => navigate('/login')} className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors">
            Back to Login
          </button>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-lg mx-auto">

          {/* Progress */}
          {step < 4 && (
            <div className="flex items-center justify-center gap-2 mb-8" data-testid="fp-progress">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                    step >= s ? 'bg-afriperx-green text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {step > s ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 3 && <div className={`w-12 h-0.5 transition-all duration-300 ${step > s ? 'bg-afriperx-green' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Enter Mobile */}
          {step === 1 && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in" data-testid="fp-step-1">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-afriperx-green/10 flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-afriperx-green" />
                </div>
                <h1 className="font-syne text-2xl font-bold text-gray-900 mb-2">Reset Your Passcode</h1>
                <p className="text-gray-500 text-sm">Enter your registered mobile number to receive a reset code via email.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    <Phone className="w-3 h-3 inline mr-1" />Mobile Number
                  </label>
                  <input
                    type="tel" value={mobile} onChange={e => setMobile(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-sm"
                    placeholder="e.g. 0714594256" data-testid="fp-mobile-input"
                  />
                </div>
                <button
                  onClick={handleSendOtp} disabled={loading}
                  className="w-full py-3.5 bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="fp-send-otp-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Send Reset Code</>}
                </button>
                <button onClick={() => navigate('/login')} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors flex items-center justify-center gap-1">
                  <ArrowLeft className="w-4 h-4" /> Back to Login
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Enter OTP */}
          {step === 2 && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in" data-testid="fp-step-2">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-afriperx-green/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-afriperx-green" />
                </div>
                <h1 className="font-syne text-2xl font-bold text-gray-900 mb-2">Enter Reset Code</h1>
                <p className="text-gray-500 text-sm">We sent a 6-digit code to <span className="font-medium text-gray-700">{maskedEmail}</span></p>
              </div>

              {!emailSent && fallbackCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-center" data-testid="fp-fallback-code">
                  <p className="text-xs text-amber-600 font-medium mb-1">Email could not be sent. Use this code:</p>
                  <p className="text-2xl font-mono font-bold text-amber-800 tracking-[0.3em]">{fallbackCode}</p>
                </div>
              )}

              {emailSent && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-center" data-testid="fp-email-sent-notice">
                  <p className="text-xs text-green-700 font-medium">Check your inbox (and spam folder) for the reset code from AfriPerx.</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Enter 6-digit code</label>
                  <input
                    type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="w-full px-4 py-4 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-center text-2xl font-mono tracking-[0.3em]"
                    placeholder="000000" data-testid="fp-otp-input"
                  />
                </div>
                <button
                  onClick={handleVerifyOtp} disabled={loading || otp.length < 6}
                  className="w-full py-3.5 bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="fp-verify-otp-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Verify Code</>}
                </button>
                <button onClick={() => setStep(1)} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors flex items-center justify-center gap-1">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Set New Passcode */}
          {step === 3 && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in" data-testid="fp-step-3">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-afriperx-gold/10 flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-afriperx-gold" />
                </div>
                <h1 className="font-syne text-2xl font-bold text-gray-900 mb-2">Set New Passcode</h1>
                <p className="text-gray-500 text-sm">Choose a new 4-digit passcode for your account.</p>
              </div>

              {suggestedPasscode && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6" data-testid="fp-suggested-passcode">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-700 font-medium mb-1">Suggested passcode</p>
                      <p className="text-2xl font-mono font-bold text-green-900 tracking-[0.3em]">{suggestedPasscode}</p>
                    </div>
                    <button
                      onClick={useSuggested}
                      className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                      data-testid="fp-use-suggested-btn"
                    >
                      <RefreshCw className="w-3 h-3" /> Use This
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">New Passcode (4 digits)</label>
                  <input
                    type="password" value={newPasscode}
                    onChange={e => setNewPasscode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength={4} inputMode="numeric"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-center text-xl font-mono tracking-[0.3em]"
                    placeholder="----" data-testid="fp-new-passcode-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Confirm Passcode</label>
                  <input
                    type="password" value={confirmPasscode}
                    onChange={e => setConfirmPasscode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength={4} inputMode="numeric"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-afriperx-green focus:ring-1 focus:ring-afriperx-green/20 outline-none text-center text-xl font-mono tracking-[0.3em]"
                    placeholder="----" data-testid="fp-confirm-passcode-input"
                  />
                </div>
                <button
                  onClick={handleResetPasscode} disabled={loading}
                  className="w-full py-3.5 bg-afriperx-gold hover:bg-afriperx-gold-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="fp-reset-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Shield className="w-4 h-4" /> Reset Passcode</>}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-afriperx-green/5 p-8 animate-fade-in text-center" data-testid="fp-success">
              <div className="w-20 h-20 rounded-full bg-afriperx-green/10 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-afriperx-green" />
              </div>
              <h1 className="font-syne text-2xl font-bold text-gray-900 mb-3">Passcode Reset!</h1>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
                Your passcode has been updated. You can now sign in with your new passcode.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-6 text-left" data-testid="fp-new-credentials">
                <p className="text-xs font-semibold text-afriperx-green uppercase tracking-wider mb-3">Your Updated Login</p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100">
                    <span className="text-xs text-gray-500">Mobile Number</span>
                    <span className="font-mono font-bold text-gray-900 text-sm" data-testid="fp-cred-mobile">{mobile}</span>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100">
                    <span className="text-xs text-gray-500">New Passcode</span>
                    <span className="font-mono font-bold text-gray-900 text-sm" data-testid="fp-cred-passcode">{newPasscode}</span>
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Please remember your new passcode.
                </p>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="w-full py-3.5 bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
                data-testid="fp-goto-login-btn"
              >
                Sign In Now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
